import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MuiAccordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import MapPlaceholder from 'components/common/address/MapPlaceholder'
import RoutingMap from 'components/customer-details/content/routing/RoutingMap'
import ServiceItemSummary from 'components/customer-details/content/routing/ServiceItem/ServiceItemSummary'
import { selectLatitude, selectLongitude } from 'data/address/addressSelectors'
import { getWorkOrderStatusDisplayLabel, WORK_ORDER_STATUS, WORK_ORDER_STATUS_DISPLAY } from 'settings/constants/service'
import { setIsAssignStopDialogOpen, setRoutingActionServiceDate, setStopToBeAssigned } from 'slices/route/routingActionsSlice'
import T from 'T'
import { createDateFromString, deserializeDate, formatDateToBEFormatDateFns } from 'utils/date'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { combineAddressInfo } from 'utils/helper'
import { getFeaturePointFromCoordsObj } from 'utils/map'
import { isValidLatitudeRange, isValidLongitudeRange } from 'utils/validations'
import { stopStatus } from 'utils/workOrderStatus'

import type { MarkerFeature } from 'components/customer-details/content/routing/RoutingMap'

type RoutingService = {
  workOrderId?: string
  workOrderStatus?: string
  completionStatus?: string
  intentStatus?: string
  sequence?: number | string
  serviceName?: string
  routeId?: string
  routeName?: string
  routeColor?: string
  measureUnit?: string
  measureVolume?: number
  materialType?: string
  materialColor?: string
  addressName?: string
  containerLatitude?: number
  containerLongitude?: number
  currentStop?: number
  stopCount?: number
}

type Props = {
  service: RoutingService
  containerCount?: number
  onItemClick?: (item: { routeId?: string }) => void
}

type RoutingState = {
  CustomerDetails?: { routing?: { serializedServiceDate?: string } }
  CustomersReducer?: { customerDetails?: { accountName?: string } }
}

const Accordion = styled(MuiAccordion, { shouldForwardProp: prop => prop !== 'borderColor' })<{ borderColor: string }>(
  ({ theme, borderColor }) => ({
    border: `2px solid ${borderColor}`,
    borderTop: `4px solid ${borderColor}`,
    borderRadius: theme.spacing(1),
    '&:first-of-type, &:last-of-type': {
      borderRadius: theme.spacing(1),
    },
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },
    '&:before': {
      display: 'none',
    },
  })
)

const AccordionSummary = styled(MuiAccordionSummary)(() => ({
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginTop: 0,
    marginBottom: 0,
  },
}))
const ServiceItem: React.FC<Props> = ({ service, containerCount, onItemClick }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [features, setFeatures] = useState<MarkerFeature[]>([])
  const serializedServiceDate = useSelector((state: RoutingState) => state.CustomerDetails?.routing?.serializedServiceDate)
  const accountName = useSelector((state: RoutingState) => state.CustomersReducer?.customerDetails?.accountName ?? '')
  const {
    workOrderId,
    intentStatus,
    sequence,
    serviceName,
    routeId,
    routeColor,
    measureUnit,
    materialType,
    materialColor,
    containerLatitude,
    containerLongitude,
  } = service
  const workOrderStatus = stopStatus(service)
  const position = sequence ?? 'U'
  const routeName = service.routeName ?? T.UNASSIGNED
  const measureVolume = service.measureVolume ?? 0
  const addressName = service.addressName ?? ''
  const currentStop = service.currentStop ?? 0
  const stopCount = service.stopCount ?? 0
  const isUnassigned = routeName === T.UNASSIGNED
  const latitude = selectLatitude(service)
  const longitude = selectLongitude(service)
  const containerPrefix = containerCount ? `${containerCount}: ` : ''
  const measure = `${containerPrefix}${measureVolume} ${getMeasureUnit(measureUnit)}`
  const addressStr = combineAddressInfo(service)
  const woStatusColor = WORK_ORDER_STATUS_DISPLAY[workOrderStatus ?? ''] ?? { borderColor: grey[400], bgColor: grey[100] }
  const woStatusBorderColor = woStatusColor.borderColor
  const woStatusBgColor = woStatusColor.bgColor
  const serviceDate = serializedServiceDate ? deserializeDate(serializedServiceDate) : new Date()
  const formattedServiceDate = formatDateToBEFormatDateFns(serviceDate)
  const hasValidLatLong = isValidLatitudeRange(latitude) && isValidLongitudeRange(longitude)
  const isInProgress = workOrderStatus === WORK_ORDER_STATUS.IN_PROCESS
  const isScheduled = workOrderStatus === WORK_ORDER_STATUS.SCHEDULED
  const showStatusBanner = !isUnassigned && !isScheduled && workOrderStatus

  const hasValidContainerLatLong = isValidLatitudeRange(containerLatitude) && isValidLongitudeRange(containerLongitude)

  const handleAssignStop = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (!workOrderId) return
      const effectiveServiceDate = formattedServiceDate ? createDateFromString(formattedServiceDate) : new Date()
      dispatch(setRoutingActionServiceDate(effectiveServiceDate))
      dispatch(
        setStopToBeAssigned({
          ...service,
          routeId,
          isUnassigned: !routeId,
          addressStr,
          id: workOrderId,
          workOrderId,
          latitude,
          longitude,
          accountName,
          toBeInserted: true,
          sequence,
        })
      )
      dispatch(setIsAssignStopDialogOpen(true))
    },
    [workOrderId, formattedServiceDate, routeId, addressStr, latitude, longitude, accountName, sequence, dispatch]
  )

  const handleSequenceClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (onItemClick) {
        onItemClick({ routeId })
      }
    },
    [onItemClick, routeId]
  )

  useEffect(() => {
    const marker = WORK_ORDER_STATUS_DISPLAY[workOrderStatus ?? '']?.marker
    const markerTextColor = WORK_ORDER_STATUS_DISPLAY[workOrderStatus ?? '']?.markerTextColor
    const currentMarkerFeature = getFeaturePointFromCoordsObj({
      latitude,
      longitude,
      properties: { position, icon: position === 'U' ? 'to-be-assigned-marker' : marker, 'text-color': markerTextColor },
    })
    const newFeatures = [currentMarkerFeature]
    if (hasValidContainerLatLong) {
      const containerMarkerFeature = getFeaturePointFromCoordsObj({
        latitude: containerLatitude,
        longitude: containerLongitude,
        properties: { icon: 'container-marker' },
      })
      newFeatures.push(containerMarkerFeature)
    }
    setFeatures(newFeatures)
  }, [])

  return (
    <>
      <Accordion borderColor={woStatusBorderColor} disableGutters elevation={0} TransitionProps={{ mountOnEnter: true }}>
        <AccordionSummary
          sx={{
            p: 0,
            '& .MuiAccordionSummary-content': { overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' },
            '& .MuiAccordionSummary-expandIconWrapper': { position: 'absolute', right: 0 },
          }}
          expandIcon={<ChevronRightIcon sx={{ fontSize: '1.5rem', color: theme.palette.action.active }} />}
        >
          <ServiceItemSummary
            addressStr={addressStr}
            addressName={addressName}
            intentStatus={intentStatus}
            serviceName={serviceName}
            workOrderStatus={workOrderStatus ?? ''}
            woStatusBorderColor={woStatusBorderColor}
            routeName={routeName}
            routeColor={routeColor}
            routeId={routeId}
            sequence={sequence}
            isUnassigned={isUnassigned}
            materialType={materialType}
            materialColor={materialColor}
            measure={measure}
            serviceDate={formattedServiceDate}
            onAssignStop={handleAssignStop}
            onSequenceClick={handleSequenceClick}
          />
          {showStatusBanner && isInProgress && (
            <Box sx={{ backgroundColor: woStatusBgColor, py: 0.5, px: 2, textAlign: 'center' }}>
              <Typography variant="body1" fontWeight={700} color="info.dark">
                {`${T.DRIVER_AT_STOP} ${currentStop} of ${stopCount}`}
              </Typography>
            </Box>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box>
            {showStatusBanner && !isInProgress && (
              <Box sx={{ backgroundColor: woStatusBgColor, py: 0.5, px: 2, textAlign: 'center' }}>
                <Typography variant="body1" fontWeight={700} color="info.dark">
                  {getWorkOrderStatusDisplayLabel(workOrderStatus, workOrderStatus)}
                </Typography>
              </Box>
            )}
            {hasValidLatLong && <RoutingMap features={features} latitude={latitude} longitude={longitude} />}
            {!hasValidLatLong && <MapPlaceholder />}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default ServiceItem
